import { IoTriangle } from 'react-icons/io5';
import Link from 'next/link';
import { TimeFrames } from '@shared/types/frontend/searchHome';
import { useRouter } from 'next/router';
import { toast } from 'sonner';
import { FaBookmark, FaRegBookmark } from 'react-icons/fa';
import { useState } from 'react';
import { IoIosArrowRoundForward } from 'react-icons/io';
import { abbreviateNumber, toSpecialFixed } from '@/lib/misc/utils';
import getTrustWalletImage from '@/lib/misc/getTrustWalletImage';
import getNetworkLogo from '@/lib/misc/getNetworkLogo';
import { useSession } from '@/contexts/LitSessionProvider';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { ImageWithFallback } from '../../../shared/NextImageFallback';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useRecentToken } from '@/hooks/saved/useRecentToken';
import { useSavedToken } from '@/hooks/saved/useSavedToken';
import classNames from '@/lib/misc/classNames';
import { haveDetails } from '@/lib/asset/nativeAddressHelper';
import DTTopTokenModal from './DTTopTokenModal';
const NormeiTable = ({
  resultsToUse,
  setKeyword,
  timeFrame,
  customHandler,
  className,
  tableRowClassName
}: {
  resultsToUse: any[] | undefined;
  setKeyword: (_: string) => void;
  timeFrame: TimeFrames;
  customHandler?: (_: any) => void;
  className?: string;
  scrollViewClassName?: string;
  tableRowClassName?: string;
}) => {
  const router = useRouter();
  const {
    PKP
  } = useSession();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    handleSaveRecent
  } = useRecentToken();
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [isModalOpen, setIsOpenModal] = useState(false);
  const [selectedTokenDetails, setSelectedTokenDetails] = useState(null);
  function toggleModal() {
    setIsOpenModal(!isModalOpen);
    setSelectedTokenDetails(null);
  }
  const _handleOnTokenSelect = searchResult => {
    const tokenItem = searchResult?.token;
    if (!selectedTokenDetails && haveDetails(tokenItem)) {
      setSelectedTokenDetails(searchResult);
      setIsOpenModal(true);
      return;
    }
    setKeyword('');
    if (!customHandler) {
      LogToMixpanel({
        pkp: PKP,
        eventName: EVENTS.search.clickToken,
        category: CATEGORIES.search,
        metaData: {
          tokenAddress: tokenItem?.address,
          networkId: tokenItem?.networkId,
          source: 'desktop'
        }
      });
      handleSaveRecent({
        tokenData: {
          address: tokenItem?.address,
          networkId: tokenItem?.networkId,
          imageLargeUrl: tokenItem?.imageLargeUrl,
          symbol: tokenItem?.symbol,
          token: tokenItem,
          timeStamp: Date.now()
        }
      });
      router.push(`/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}&tradeSide=buy`);
    } else {
      const _token = {
        ...tokenItem,
        imageLargeUrl: tokenItem?.info?.imageThumbUrl
      };
      customHandler(_token);
    }
  };
  const {
    checkIsSaved,
    handleSaveSingle,
    handleDeleteTokenFromHome
  } = useSavedToken({});
  return <div className={classNames('h-full no-scrollbar ', className)} data-sentry-component="NormeiTable" data-sentry-source-file="NormeiTable.tsx">
      <Table className='no-scrollbar' data-sentry-element="Table" data-sentry-source-file="NormeiTable.tsx">
        <TableBody className='no-scrollbar' data-sentry-element="TableBody" data-sentry-source-file="NormeiTable.tsx">
          {resultsToUse && resultsToUse.length > 0 && <>
              {resultsToUse.map((searchResult, index) => {
            const tokenItem = searchResult?.token;
            const isMultiToken = haveDetails(tokenItem);
            const _tokenPriceUsd = searchResult?.priceUSD ? searchResult?.priceUSD : searchResult?.prices?.length > 0 ? searchResult?.prices[0]?.priceUsd : 0;
            const change = parseFloat(searchResult?.[`change${timeFrame}`] ?? searchResult?.tokenInfo?.results[0]?.[`change${timeFrame}`]);
            const _marketCap = searchResult?.marketCap ?? searchResult?.tokenInfo?.results[0]?.marketCap;
            const _tokenImg = (tokenItem?.imageLargeUrl || tokenItem?.info?.imageThumbUrl || tokenItem?.imageThumbUrl) ?? getTrustWalletImage(tokenItem?.networkId, tokenItem?.address);
            return <TableRow key={`${tokenItem?.name}-${index}`} className={classNames('px-4 hover:cursor-pointer hover:bg-genius-blue  w-full h-[80px] flex items-center justify-between no-scrollbar', tableRowClassName)} onMouseEnter={() => setHoveredRowIndex(index)} onMouseLeave={() => setHoveredRowIndex(null)} onClick={() => customHandler ? _handleOnTokenSelect(searchResult) : undefined}>
                    {/* token and network image, name symbol */}
                    <TableCell className='flex items-center w-[10px]'>
                      <div onClick={() => {
                  if (!PKP) {
                    toast.error('Create an account to save tokens to your watchlist.', {
                      duration: 5000
                    });
                    return;
                  }
                  if (!checkIsSaved(tokenItem?.address, tokenItem?.networkId)) {
                    LogToMixpanel({
                      pkp: PKP,
                      eventName: EVENTS.asset.saveToken,
                      category: CATEGORIES.asset,
                      metaData: {
                        address: tokenItem?.address
                      }
                    });
                    handleSaveSingle({
                      tokenToSave: tokenItem
                    });
                  } else {
                    handleDeleteTokenFromHome(tokenItem);
                  }
                }} className='flex items-center mr-4'>
                        {checkIsSaved(tokenItem?.address, tokenItem?.networkId) ? <FaBookmark className='w-[12px] h-[12px] text-genius-cream cursor-pointer' /> : <FaRegBookmark className='w-[12px] h-[12px] text-genius-cream cursor-pointer' />}
                      </div>
                    </TableCell>
                    <TableCell className='flex items-center w-2/4'>
                      <Link onClick={e => {
                  if (customHandler || isMultiToken) {
                    e.preventDefault(); // Prevent navigation
                    _handleOnTokenSelect(searchResult); // Trigger custom logic if needed
                  }
                }} className='flex items-center w-full' href={customHandler || isMultiToken ? '#' : `/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}&tradeSide=buy`}>
                        <div className='flex items-center'>
                          <div className='flex items-center gap-3 relative'>
                            <ImageWithFallback src={_tokenImg} width={24} height={24} alt='token-logo' className='rounded-full w-[32px] h-[32px]' />
                            <ImageWithFallback src={getNetworkLogo(tokenItem?.networkId)} width={10} height={10} alt='network-logo' className='w-[16px] h-[16px] bottom-0 left-4 absolute shrink-0' />
                            <div className='flex flex-col gap-y-2 justify-start text-left'>
                              <div className='text-base/4 text-genius-cream max-w-[200px] truncate'>
                                {tokenItem?.name}
                              </div>
                              <div className='text-xs/3 text-genius-cream/50 flex items-center'>
                                {tokenItem?.symbol}
                                {haveDetails(tokenItem) && <div className='flex items-center text-[12px] text-genius-cream/50 ml-2'>
                                    Details <IoIosArrowRoundForward className='w-[16px] h-[16px]' />{' '}
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </TableCell>
                    {/* token price */}
                    <TableCell className='w-1/4'>
                      <Link onClick={e => {
                  if (customHandler || isMultiToken) {
                    e.preventDefault(); // Prevent navigation
                    _handleOnTokenSelect(searchResult); // Trigger custom logic if needed
                  }
                }} className='flex items-center' href={customHandler ? '' : `/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}&tradeSide=buy`}>
                        <div className='flex flex-col text-left  min-w-24'>
                          <p className='text-base/4  text-genius-cream text-right mb-[6px] mt-[1px]'>{`$${parseFloat(_tokenPriceUsd) >= 1000000 ? abbreviateNumber(_tokenPriceUsd) : toSpecialFixed(_tokenPriceUsd, 2)?.value}`}</p>
                          <div className='flex  items-center justify-end text-right'>
                            {change >= 0 ? <div className='flex items-center text-right'>
                                <IoTriangle color='#34D399' size={12} />
                                <p className='text-gray-400 ml-1 text-right text-[12px]'>{`${toSpecialFixed(change * 100, 2)?.value}%`}</p>
                              </div> : <div className='flex items-center text-right'>
                                <IoTriangle color='#FF5B5B' size={12} className='transform rotate-180' />
                                <p className='text-red-400 ml-1 text-right text-[12px]'>{`${toSpecialFixed(Math.abs(change * 100), 2).value}%`}</p>
                              </div>}
                          </div>
                        </div>
                      </Link>
                    </TableCell>
                    {/* token market cap*/}
                    <TableCell className='w-1/4'>
                      <Link onClick={e => {
                  if (customHandler || isMultiToken) {
                    e.preventDefault(); // Prevent navigation
                    _handleOnTokenSelect(searchResult); // Trigger custom logic if needed
                  }
                }} className='flex items-center' href={customHandler ? '' : `/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}&tradeSide=buy`}>
                        <div className='grid gap-2 text-genius-cream text-right'>
                          <p className='text-base/4'>${abbreviateNumber(_marketCap)}</p>
                          <p className='text-xs opacity-50'>Market Cap</p>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell align='right'>
                      <Link onClick={e => {
                  if (customHandler || isMultiToken) {
                    e.preventDefault(); // Prevent navigation
                    _handleOnTokenSelect(searchResult); // Trigger custom logic if needed
                  }
                }} href={customHandler ? '' : `/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}&tradeSide=buy`}>
                        <Button className={`rounded-[8px] w-[84px] h-[36px] px-[26px] py-[16px] border ${hoveredRowIndex === index ? 'bg-genius-pink text-genius-blue' : 'bg-genius-blue text-genius-cream'}`}>
                          Trade
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>;
          })}
            </>}
        </TableBody>
      </Table>
      {selectedTokenDetails && isModalOpen && <DTTopTokenModal onTokenClick={() => _handleOnTokenSelect(selectedTokenDetails)} isModalOpen={isModalOpen} toggleModal={toggleModal} token={selectedTokenDetails} change={parseFloat(selectedTokenDetails?.[`change${timeFrame}`] ?? selectedTokenDetails?.tokenInfo?.results[0]?.[`change${timeFrame}`])} />}
    </div>;
};
export default NormeiTable;